import { useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleBody from '@/components/feature/ArticleBody';
import ShareBar from '@/components/feature/ShareBar';
import RelatedSection from '@/components/feature/RelatedSection';
import HotSidebar from '@/components/feature/HotSidebar';
import NotFoundState from '@/components/feature/NotFoundState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { formatViews, getItemById, getItemsByType, getRelated, sortByTime } from '@/utils/content';

export default function NewsDetail() {
  const { id = '' } = useParams();
  const item = getItemById('news', id);

  useDocumentMeta({
    title: item ? `【${item.title}】 - 91网站` : '资讯详情 - 91网站',
    description: item ? item.summary : '91网站新闻资讯详情页。',
    keywords: item ? item.tags?.join(',') || '91网站,新闻资讯' : '91网站,新闻资讯',
  });

  if (!item) {
    return (
      <SiteLayout>
        <NotFoundState
          message="该新闻内容不存在或已删除，请返回新闻频道浏览其他资讯。"
          backTo="/category/news"
          backLabel="返回新闻频道"
          secondaryTo="/"
          secondaryLabel="返回首页"
        />
      </SiteLayout>
    );
  }

  const related = getRelated(item, 4);
  const hot = sortByTime(getItemsByType('news'))
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 6);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '新闻频道', to: '/category/news' }, { label: item.title }]} />

        <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-8">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                {item.subCategory}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                {item.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-secondary-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-building-2-line"></i>
                  来源：{item.source}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {item.publishTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(item.views)} 阅读
                </span>
              </div>

              <div className="mt-6">
                <ArticleBody content={item.content} image={item.image} imageAlt={item.title} />
              </div>

              {item.tags && item.tags.length > 0 && (
                <div className="mt-8 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-secondary-500">标签：</span>
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-6 border-t border-background-200 pt-5">
                <ShareBar title={item.title} />
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <HotSidebar title="热门阅读" items={hot} />
            <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-accent-500"></i>
                权威门户提示
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-secondary-500">
                本站资讯为综合门户聚合内容，仅供信息参考。请以权威部门发布为准，不构成任何投资或决策建议。
              </p>
            </div>
          </aside>
        </div>

        <RelatedSection title="相关新闻推荐" items={related} moreTo="/category/news" />
      </div>
    </SiteLayout>
  );
}